import { useEffect, useState } from "react";
import { Footer } from "@/components/landing/Footer";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ShieldAlert, Terminal } from "lucide-react";
import { useSeo } from "@/lib/seo";

const UPDATED = "AUG 2026";

const SECTIONS = [
  {
    id: "acceptance",
    num: "00",
    title: "Acceptance of Terms",
    body: [
      "These Terms of Service and the Risk Notice below (collectively, the \"Terms\") govern your access to and use of the TradrQuant website, Telegram channels, alerts, dashboards, screeners, e-books, and any related software or services (collectively, the \"Service\"), operated by TradrQuant (\"TradrQuant,\" \"we,\" \"us,\" or \"our\").",
      "By accessing, subscribing to, or using the Service in any way — including joining a free Telegram channel — you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, do not use the Service.",
      "You must be at least 18 years of age (or the age of legal majority in your jurisdiction) and legally able to enter into a binding contract to use the Service.",
    ],
  },
  {
    id: "not-advice",
    num: "01",
    title: "Not Financial, Investment or Trading Advice",
    body: [
      "TradrQuant is a technology and education company. We are NOT a registered investment adviser, broker-dealer, commodity trading advisor (CTA), commodity pool operator (CPO), futures commission merchant, financial planner, or a licensed financial professional of any kind, and we are not registered with the U.S. Securities and Exchange Commission (SEC), the Commodity Futures Trading Commission (CFTC), the Financial Industry Regulatory Authority (FINRA), the National Futures Association (NFA), or any comparable regulator in any jurisdiction.",
      "Nothing on the Service constitutes — and nothing should be construed as — investment advice, financial advice, trading advice, a recommendation, a solicitation, or an offer to buy or sell any security, cryptocurrency, futures contract, option, derivative, or other financial instrument. All content, pattern detections, alerts, scores, signals, and commentary are provided for general informational and educational purposes only.",
      "We do not know you, your financial situation, your risk tolerance, your investment objectives, or your tax circumstances. No content is personalized to you. You are solely responsible for your own decisions. Before acting on any information, consult a licensed financial advisor, broker, attorney, and/or tax professional.",
    ],
  },
  {
    id: "risk",
    num: "02",
    title: "Risk Disclosure — Trading Involves Substantial Risk",
    body: [
      "TRADING AND INVESTING INVOLVE A SUBSTANTIAL RISK OF LOSS AND ARE NOT SUITABLE FOR EVERY INVESTOR. You may lose some or ALL of your invested capital. Never trade with money you cannot afford to lose. Leverage and margin can work against you as well as for you and can amplify losses far beyond your initial deposit.",
      "Cryptocurrency and digital-asset markets are highly volatile, largely unregulated, and can move dramatically in short periods. Digital assets are not legal tender, are not backed by any government, and are not covered by deposit-insurance or investor-protection schemes such as FDIC or SIPC. The value of a digital asset can go to zero.",
      "The volatility and unpredictability of the price of digital assets and other instruments may result in significant loss over a short period of time. There is no guarantee that any strategy, pattern, or signal will be profitable or will avoid losses.",
    ],
  },
  {
    id: "performance",
    num: "03",
    title: "Past & Hypothetical Performance",
    body: [
      "PAST PERFORMANCE IS NOT INDICATIVE OF FUTURE RESULTS. Any historical returns, win rates, expected values, completion rates, or examples presented anywhere on the Service are provided for illustrative and educational purposes only and do not represent, and are not a guarantee of, actual or future performance.",
      "CFTC RULE 4.41 — HYPOTHETICAL OR SIMULATED PERFORMANCE RESULTS HAVE CERTAIN LIMITATIONS. Unlike an actual performance record, simulated results do not represent actual trading. Also, because the trades have not actually been executed, the results may have under- or over-compensated for the impact, if any, of certain market factors, such as lack of liquidity. Simulated trading programs in general are also subject to the fact that they are designed with the benefit of hindsight. No representation is being made that any account will or is likely to achieve profit or losses similar to those shown.",
      "Statistics attributed to third-party sources (e.g., Bulkowski / thepatternsite.com) reflect that source's methodology and sample and are cited for educational reference only. Sample outputs, mock feeds, and demonstrations shown on the Service are illustrative and may not reflect live results.",
    ],
  },
  {
    id: "no-guarantee",
    num: "04",
    title: "No Guarantees & No Fiduciary Duty",
    body: [
      "We make no representation, warranty, or guarantee regarding the accuracy, completeness, timeliness, or reliability of any information, alert, or detection provided through the Service. Signals may be delayed, incomplete, or wrong. Automated detections can and do produce false positives.",
      "Your use of the Service does not create any advisory, fiduciary, brokerage, agency, or confidential relationship between you and TradrQuant. We owe you no fiduciary duty. You alone are responsible for evaluating the merits and risks of any decision.",
    ],
  },
  {
    id: "no-testimonials",
    num: "05",
    title: "Testimonials & Third-Party Content",
    body: [
      "Any testimonials or results shared by users or the founder are individual experiences, reflecting real-life experiences of those who have used our Service. They are individual results and results do vary. We do not claim that they are typical results. Testimonials are not necessarily representative of all who will use the Service and are not a guarantee of future performance or success.",
      "The Service may contain links to third-party websites, exchanges, tools, or content (including Telegram, exchanges, verification pages, and e-book hosts). We do not control and are not responsible for the content, accuracy, availability, security, or practices of any third party. Accessing third-party services is at your own risk and subject to their terms.",
    ],
  },
  {
    id: "license",
    num: "06",
    title: "License, Acceptable Use & Intellectual Property",
    body: [
      "Subject to these Terms, we grant you a limited, personal, non-exclusive, non-transferable, revocable license to access and use the Service for your own personal, non-commercial use. All content, software, trademarks, logos, and materials are owned by TradrQuant or its licensors and are protected by intellectual-property laws.",
      "You agree not to: (a) resell, redistribute, republish, or share alerts, signals, or content with non-subscribers; (b) scrape, reverse-engineer, or copy the Service; (c) use the Service for any unlawful purpose or in violation of any applicable law, regulation, or exchange rule; or (d) misrepresent your identity or affiliation. We may suspend or terminate access for any violation.",
    ],
  },
  {
    id: "payment",
    num: "07",
    title: "Subscriptions, Payments & Cancellation",
    body: [
      "Paid features (including any future \"Founder Terminal\") are billed on the terms disclosed at the point of purchase. Unless required by law, fees are non-refundable. You may cancel a recurring subscription at any time; cancellation stops future renewals and takes effect at the end of the current billing period. Prices and features may change on a prospective basis.",
    ],
  },
  {
    id: "warranty",
    num: "08",
    title: "Disclaimer of Warranties",
    body: [
      "THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE,\" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. We do not warrant that the Service will be uninterrupted, secure, error-free, or free of viruses or other harmful components, or that any signal or data will be accurate or available.",
    ],
  },
  {
    id: "liability",
    num: "09",
    title: "Limitation of Liability",
    body: [
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL TRADRQUANT, ITS FOUNDER, OFFICERS, EMPLOYEES, CONTRACTORS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, TRADING LOSSES, LOSS OF DATA, OR LOSS OF GOODWILL, ARISING OUT OF OR RELATING TO YOUR USE OF (OR INABILITY TO USE) THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
      "OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE THREE (3) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS (USD $100). Some jurisdictions do not allow certain limitations, so some of the above may not apply to you.",
    ],
  },
  {
    id: "indemnity",
    num: "10",
    title: "Indemnification",
    body: [
      "You agree to indemnify, defend, and hold harmless TradrQuant and its founder, affiliates, and personnel from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Service, your trading or investment decisions, your violation of these Terms, or your violation of any law or the rights of a third party.",
    ],
  },
  {
    id: "governing",
    num: "11",
    title: "Governing Law, Arbitration & Changes",
    body: [
      "These Terms are governed by the laws of the applicable jurisdiction in which TradrQuant operates, without regard to conflict-of-laws principles. Any dispute shall, to the extent permitted by law, be resolved by binding individual arbitration, and you waive any right to participate in a class action. You and TradrQuant agree to first attempt to resolve any dispute informally.",
      "We may update these Terms at any time by posting the revised version with a new effective date. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms. If any provision is held unenforceable, the remaining provisions remain in full force.",
    ],
  },
  {
    id: "contact",
    num: "12",
    title: "Contact",
    body: [
      "Questions about these Terms or the Risk Notice can be directed to the TradrQuant team via our official Telegram channels. TradrQuant · @Tradernaut.",
    ],
  },
];

const useActiveSection = () => {
  const [active, setActive] = useState(SECTIONS[0].id);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);
  return active;
};

export default function Legal() {
  const active = useActiveSection();
  const [cursor, setCursor] = useState(true);
  useSeo({
    title: "Risk Notice & Terms of Service — TradrQuant",
    description: "TradrQuant Risk Notice & Terms of Service. Not financial advice. Trading involves substantial risk — read our full disclosures before using the service.",
    path: "/risk",
  });
  useEffect(() => {
    const iv = setInterval(() => setCursor((c) => !c), 530);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "TradrQuant · Risk Notice & Terms";
  }, []);

  return (
    <div data-testid="legal-page" className="grain relative min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      {/* header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" data-testid="legal-home-link" className="flex items-center gap-2.5 group">
            <img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto group-hover:scale-105 transition-transform duration-300" />
            <span data-testid="wordmark" className="tq-wordmark text-[#ECEBE4] text-[19px] sm:text-[21px]">Tradr<span className="tq-q">Q</span>uant</span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-btn"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 border border-white/15 px-3 py-2 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors duration-300"
          >
            <ChevronLeft size={14} /> cd ~/terminal
          </Link>
        </div>
      </header>

      {/* hero terminal window */}
      <div className="mx-auto max-w-[1200px] px-5 md:px-10 pt-10 md:pt-16">
        <div className="relative border border-white/12 bg-[#0d0d0d]">
          <div className="flex items-center gap-2 border-b border-white/10 bg-[#111111] px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#BACD1C]/60" />
            <span className="ml-3 font-mono text-xs text-white/45 tracking-[0.15em] lowercase">legal/risk_notice.sh</span>
            <span className="ml-auto flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7D8390]">
              <ShieldAlert size={13} /> read me
            </span>
          </div>
          <div className="p-6 md:p-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">// mandatory disclosure</p>
            <h1 className="mt-4 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Risk Notice <span className="text-white/30">&</span><br />
              <span className="text-[#7D8390]">Terms of Service</span>
              <span className="text-[#7D8390]">{cursor ? "_" : "\u00A0"}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-sm text-white/50 leading-relaxed">
              Read this before you use anything. TradrQuant is an education and technology
              company — not a financial advisor. Trading is risky and you can lose money.
              This page tells you exactly what we are, what we are not, and where the risk sits.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/35">
              <span>&gt; last_updated: <span className="text-[#BACD1C]/80">{UPDATED}</span></span>
              <span>&gt; status: <span className="text-[#BACD1C]/80">active</span></span>
              <span>&gt; not_financial_advice: <span className="text-[#BACD1C]/80">true</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* body */}
      <div className="mx-auto max-w-[1200px] px-5 md:px-10 py-12 md:py-16 flex flex-col lg:flex-row gap-10 lg:gap-16">
        {/* index */}
        <aside className="lg:w-64 shrink-0">
          <div className="lg:sticky lg:top-24">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/30 mb-4">
              <Terminal size={13} /> // index
            </p>
            <nav className="flex lg:flex-col gap-x-4 gap-y-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="legal-index">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`legal-index-${s.id}`}
                  className={`shrink-0 font-mono text-xs tracking-[0.1em] py-1.5 border-l-2 pl-3 transition-colors duration-300 ${
                    active === s.id
                      ? "border-[#BACD1C] text-[#BACD1C]"
                      : "border-white/10 text-white/45 hover:text-white/80 hover:border-white/30"
                  }`}
                >
                  <span className="text-white/25 mr-2">{s.num}</span>
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* sections */}
        <main className="min-w-0 flex-1 space-y-14">
          {SECTIONS.map((s) => (
            <motion.section
              key={s.id}
              id={s.id}
              data-testid={`legal-section-${s.id}`}
              className="scroll-mt-24"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">
                [{s.num}] // section.{s.id.replace(/-/g, "_")}()
              </p>
              <h2 className="mt-3 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl leading-tight">
                {s.title}
              </h2>
              <div className="mt-5 space-y-4 border-l border-white/10 pl-5">
                {s.body.map((p, i) => (
                  <p key={i} className="text-[13px] sm:text-sm text-white/55 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </motion.section>
          ))}

          <div className="border border-white/[0.10] bg-white/[0.03] p-6">
            <p className="text-[13px] sm:text-sm text-white/70 leading-relaxed">
              <span className="text-[#BACD1C] font-bold">// ACKNOWLEDGEMENT.</span> By using TradrQuant you
              confirm you have read and understood this Risk Notice and Terms of Service, that you are
              trading with capital you can afford to lose, and that TradrQuant is not responsible for
              your trading or investment decisions.
            </p>
          </div>

          <p className="font-mono text-xs text-white/25 tracking-[0.15em]">
            © {new Date().getFullYear()} TradrQuant · Not financial advice · Trade at your own risk
          </p>
        </main>
      </div>
      <Footer />
    </div>
  );
}
